/*
 * App-wide bot behavior detection. Composes one hook per behavior so each piece
 * of detection logic stays isolated and independently testable. Called once
 * from pages/_app.tsx.
 *
 * Easel navigates via full page loads (not client-side routing), so this hook
 * remounts on every page. A single shared `report` callback dedupes per page:
 * the first behavior to detect a bot sends the event, and any further detections
 * on the same page are suppressed. The flag resets on the next navigation, so
 * each page can report once.
 *
 * To add a new behavior: create hooks/botDetection/use<Behavior>BotCheck.ts,
 * accept `report` as its argument, and call it here.
 */
import { useCallback, useRef } from 'react'
import { trackBotTraffic, TrackBotTrafficAction } from 'lib/bots/trackBotTraffic'
import useUserAgentBotCheck from 'hooks/botDetection/useUserAgentBotCheck'
import useMouseBehaviorBotCheck from 'hooks/botDetection/useMouseBehaviorBotCheck'
import useKeyboardBehaviorBotCheck from 'hooks/botDetection/useKeyboardBehaviorBotCheck'
import usePasteBehaviorBotCheck from 'hooks/botDetection/usePasteBehaviorBotCheck'
import useScrollBehaviorBotCheck from 'hooks/botDetection/useScrollBehaviorBotCheck'
import useTimingBehaviorBotCheck from 'hooks/botDetection/useTimingBehaviorBotCheck'

const useBotDetection = (): void => {
  const hasReportedOnPage = useRef(false)

  const report = useCallback((action: TrackBotTrafficAction) => {
    if (hasReportedOnPage.current) {
      return
    }
    hasReportedOnPage.current = true
    trackBotTraffic(action)
  }, [])

  useUserAgentBotCheck(report)
  useMouseBehaviorBotCheck(report)
  useKeyboardBehaviorBotCheck(report)
  usePasteBehaviorBotCheck(report)
  useScrollBehaviorBotCheck(report)
  useTimingBehaviorBotCheck(report)
}

export default useBotDetection
